/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import _get from 'lodash/get'
import classNames from 'classnames/bind'
import { Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap'
import { withLocalization } from 'hocs/localization'
import { State } from 'elements/Input'
import Icon from 'elements/Icon'
import SelectItem from './SelectItem'
import * as styles from './Select.scss'

const cx = classNames.bind(styles)

// may be added
declare type StyleType = 'headerLang' | 'default' | 'filter' | 'pagination' | 'warning'

interface IOption {
  label: string,
  value: any,
}

interface IParamLabelRenderer {
  label: string,
  value: any,
  isMenu?: boolean,
}

interface IProps{
  name?: string,
  title?: string,
  state?: State,
  styleType?: StyleType,
  id?: string,
  dropDownAlignment?: string,
  children: React.ReactNode,
  onChange?: (param: any) => void,
  value?: any,
  options?: IOption[],
  labelRenderer?: (params: IParamLabelRenderer) => React.ReactChild,
  placeholder?: string,
  size: 'medium' | 'large',
  disabled?: boolean,
  onFocus: () => void,
  onBlur: () => void,
}

interface IState {
  isOpen: boolean,
  selectOptions: any,
  inputValue: string,
}

class Select extends React.Component<IProps, IState> {
  private labelRef = React.createRef()
  private inputRef = React.createRef()
  private menuRef = React.createRef()

  public state = {
    isOpen: false,
    selectOptions: [],
    inputValue: '',
  }

  private onLabelKeyDown = event => {
    event.stopPropagation()

    if (event.keyCode === 13) {
      event.preventDefault()
      if (this.state.isOpen) {
        this.onClose(() => this.labelRef.current.focus())
      } else {
        this.onOpen()
      }
    }
  }

  private onInputKeyDown = event => {
    event.stopPropagation()

    if (event.keyCode === 38 || event.keyCode === 40) {
      event.preventDefault()
      const firstItem = this.menuRef.current.children[0]
      firstItem && firstItem.focus()
    }
  }

  private onToggle = event => {
    if (event.type !== 'click') {
      event.stopPropagation()
      return
    }

    if (!this.state.isOpen) {
      this.onOpen()
      this.inputRef.current.focus()
    } else {
      this.onClose()
    }
  }

  private onOpen = () => {
    this.setState({
      isOpen: true,
    })
  }

  private onClose = callback => {
    const { onBlur } = this.props
    onBlur && onBlur()
    this.setState({
      isOpen: false,
    }, () => callback && callback())
  }

  private onChangeInput = ({ target }) => {
    this.onOpen()
    const { value } = target
    const { onChange, name, options, t } = this.props
    onChange && onChange({ name, value: null })
    const selectOptions = options.filter(({ label }) =>
      label.toString().toLowerCase().includes(value.toString().toLowerCase()),
    )
    const selectedValue = options.find(({ label }) => {
      return label.toString().toLowerCase() === value.toString().toLowerCase()
    })
    if (!selectOptions.length) {
      selectOptions.push({ label: t('noResult'), disabled: true, value: null })
    }
    this.setState({
      inputValue: target.value,
      selectOptions,
    })
    if (selectedValue) {
      this.setState({
        inputValue: selectedValue.label,
      })
      onChange && onChange({ name, value: selectedValue.value })
      this.onClose()
    }
  }

  private onChange = ({ value }) => {
    const { onChange, name, options } = this.props
    const currentItem = options.find(item => item.value === value)
    this.setState({
      inputValue: currentItem.label,
      selectOptions: options,
    })
    onChange && onChange({ name, value })
    this.onClose(() => this.labelRef.current.focus())
  }

  public componentDidMount () {
    let inputValue = ''
    const value = _get(this.props, 'value')
    const currentValue = value !== undefined && value.toString()
    const currentItem = this.props.options.find(item => {
      return item.value !== undefined && item.value.toString() === currentValue
    })
    inputValue = this.props.title || _get(currentItem, 'label') || ''
    this.setState({
      inputValue,
      selectOptions: this.props.options,
    })
  }

  public render () {
    const {
      title,
      id,
      state = 'default',
      styleType = 'default',
      dropDownAlignment = 'left',
      value,
      options = [],
      placeholder = 'Select...',
      size = 'medium',
      disabled = false,
    } = this.props
    const {
      isOpen,
      selectOptions,
      inputValue,
    } = this.state

    const currentValue = value !== undefined && value.toString()
    const currentItem = options.find(item => item.value !== undefined && item.value.toString() === currentValue)
    const activeTitle = title || _get(currentItem, 'label') || placeholder

    return (
      <React.Fragment>
        {disabled ? (
          <input
            className={styles.disabled}
            type='text'
            disabled
            value={activeTitle} />
        ) : (
          <div
            className={cx(
              'root',
              styleType,
              size,
              {
                disabled,
              },
            )}>
            <Dropdown
              id={id}
              isOpen={isOpen}
              toggle={this.onToggle}
            >
              <DropdownToggle tag='div' disabled={disabled}>
                <div
                  ref={this.labelRef}
                  className={cx(
                    'labelWrapper',
                    state,
                    {
                      disabled,
                      selected: !!currentItem,
                    },
                  )}
                  onKeyDown={this.onLabelKeyDown}
                >
                  <input
                    ref={this.inputRef}
                    className={cx(
                      'input',
                      {
                        selected: value,
                      },
                    )}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={this.onChangeInput}
                    onKeyDown={this.onInputKeyDown}
                  />
                  <span className={styles.icon}>
                    <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} />
                  </span>
                </div>
              </DropdownToggle>
              <DropdownMenu
                right={dropDownAlignment === 'right'}
                className={styles.menu}
              >
                <div ref={this.menuRef}>
                  {isOpen && selectOptions && selectOptions.map(item => (
                    <SelectItem
                      key={item.value}
                      onChange={this.onChange}
                      active={currentValue === item.value}
                      value={item.value}
                      disabled={item.disabled}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </div>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </React.Fragment>
    )
  }
}

export default withLocalization('select')(Select)
